import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { TbCalendar, TbFolder } from 'react-icons/tb';
import { Tooltip } from '@mantine/core';
import logoUrl from '../../../assets/icons/logo-new.png';
import Profile from '../Sidebar/Profile';
import { useWorkflowChrome } from '../WorkflowChromeContext';
import {
  useGetApproverWorkflows,
  useGetWorkflows,
  useGetWorkflowById
} from '../../../services/workflow.services';
import {
  useUserGroups,
  useUpdateUserCompany,
  useUserCompaniesCurrentUserAdminState
} from '../../../services/user.service';
import authStore from '../../../store/auth';
import { queryClient } from '../../../config/queryClient';
import { GET_WORKFLOW } from '../../../consts/queryKeys';
import { QUERY_KEYS } from '../../../services/constants';
import toast from 'react-hot-toast';
import { CompanyBreadcrumb, ProjectBreadcrumb, PeriodBreadcrumb } from './breadcrumbs';
import { checkToRedirect } from '../../../utils/checkToRedirect';
import {
  findExtractLocation,
  getStageFromLocation,
  mapExtractAcrossPeriods,
  mapFileSheetAcrossPeriods,
  mapFileSheetByMappings,
  pickPeriodSwitchDestination,
  buildStagePath,
  stageCarriesSheet,
  withFileSheet,
  withQuery,
  WORKFLOW_STAGE_KEYS
} from '../../../utils/workflowNavigation';
import { jurisdictionService } from '../../../services/jurisdiction.service';
import { useOpenApproverReport } from '../../../hooks/useOpenApproverReport';
import { periodService } from '../../../services/period.service';
import { workflowService } from '../../../services/workflow.services';
import { automationService } from '../../../services/automation.service';
import { GET_WORKFLOW_EXTRACT } from '../../../consts/queryKeys';
import { REPORT_TYPES } from '../../../consts/reportTypes';
import { permissionPolicies } from '../../../permissions/policies';
import {
  byNewest,
  deadlineLabel,
  jurisdictionDeadline
} from '../../../pages/Project/ProjectListV2/periodSteps';
import { DEADLINE_TIP_PROPS, DeadlineTip } from '../../../pages/Project/ProjectListV2/DeadlineTip';
import { NewPeriodBlock } from '../../../pages/Project/QuartersV2/NewPeriodBlock';
import { TopBar, BreadcrumbGroup, Separator, RightSlot, ProfileSlot, Chip, DueIcon } from './style';

export interface AppTopBarProps {
  /** Override company label (otherwise read from context). */
  companyLabel?: string;
  /** Override project label (otherwise read from context). */
  projectLabel?: string;
  /** Override period label (otherwise read from context). */
  periodLabel?: string;
  /** Override visibility of the company breadcrumb. */
  showCompany?: boolean;
  /** Override visibility of the comments icon. */
  showComments?: boolean;
  /** Override comments icon click handler. */
  onCommentsClick?: () => void;
}

const AppTopBar: React.FC<AppTopBarProps> = props => {
  const ctx = useWorkflowChrome();
  const navigate = useNavigate();
  const location = useLocation();
  const activeCompanyIdRef = useRef<string | null>(localStorage.getItem('activeCompanyId'));

  // Datox-internal users (email ending with @datox.ai) can switch the active
  // company — everyone else is implicitly scoped to their own company and
  // doesn't see the chip at all.
  const userEmail =
    (authStore?.user as unknown as { username?: string } | undefined)?.username ?? '';
  const isDatoxUser = userEmail.toLowerCase().endsWith('@datox.ai');

  const { data: currentUserState, isLoading: isLoadingCurrentUser } =
    useUserCompaniesCurrentUserAdminState() as {
      data?: { role?: string; azure_object_id?: string };
      isLoading: boolean;
    };
  const isApprover = currentUserState?.role === 'Approver';

  const { data: userGroups, refetch: refetchUserCompanies } = useUserGroups({
    queryOptions: { enabled: isDatoxUser && !isLoadingCurrentUser && !isApprover }
  }) as { data?: any; refetch: () => void };
  const updateUserCompany = useUpdateUserCompany();

  const companyOptions = useMemo(() => {
    // Runtime shape is `{ company_id, group_name }[]` — Sidebar/Profile/Menu
    // reads the same `group_name` field. (`company_name` does not exist in
    // the response; using it left the label empty and fell back to "Company".)
    const groups = (userGroups?.user_groups as Array<any>) ?? [];
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return groups
      .map(c => ({
        value: c?.company_id as string,
        label: (c?.group_name ?? c?.company_name) as string
      }))
      .filter(
        opt => opt.value && opt.label && opt.label.trim() !== '' && !UUID_RE.test(opt.label.trim())
      );
  }, [userGroups]);

  const currentCompanyId = userGroups?.current_company_id as string | undefined;
  const currentCompanyName = useMemo(() => {
    if (!currentCompanyId) return undefined;
    const found = companyOptions.find(c => c.value === currentCompanyId);
    return found?.label;
  }, [companyOptions, currentCompanyId]);

  const handleSelectCompany = (companyId: string) => {
    if (companyId === currentCompanyId) {
      // Clicking the already-active company is treated as "back to project
      // list" — useful for users deep inside a workflow who want to exit.
      navigate('/project');
      return;
    }
    updateUserCompany.mutate(
      { data: { company_id: companyId } },
      {
        onSuccess: () => {
          // The API request interceptor reads activeCompanyId from
          // localStorage to set the X-Active-Company-Id header — same
          // pattern as the legacy Profile menu's company switcher.
          activeCompanyIdRef.current = companyId;
          localStorage.setItem('activeCompanyId', companyId);
          toast.success('The company has been successfully switched');
          authStore.setWorkflow(null, '');
          queryClient.removeQueries(GET_WORKFLOW);
          queryClient.removeQueries('WORKFLOW_ACCESS_GUARD');
          queryClient.removeQueries(QUERY_KEYS.GET_USER_COMPANIES_ALL_USERS);
          queryClient.invalidateQueries();
          queryClient.refetchQueries(QUERY_KEYS.GET_USER_COMPANIES_CURRENT_USER_ADMIN_STATE, {
            active: true
          });
          refetchUserCompanies();
          // Send the user back to the projects list — the previous
          // workflow/period IDs belong to the old company and would 404.
          navigate('/project');
        },
        onError: (err: any) => {
          toast.error(`Update company ${err?.data?.detail ?? 'failed'}`);
        }
      }
    );
  };

  useEffect(() => {
    const handleActiveCompanyStorageChange = (event: StorageEvent) => {
      if (event.key !== 'activeCompanyId') return;
      if (event.newValue === activeCompanyIdRef.current) return;

      activeCompanyIdRef.current = event.newValue;
      authStore.setWorkflow(null, '');
      queryClient.clear();
      window.location.replace('/project');
    };

    window.addEventListener('storage', handleActiveCompanyStorageChange);

    return () => {
      window.removeEventListener('storage', handleActiveCompanyStorageChange);
    };
  }, []);

  const companyLabel = props.companyLabel ?? ctx.companyLabel ?? currentCompanyName;
  const projectLabel = props.projectLabel ?? ctx.projectLabel;
  const periodLabel = props.periodLabel ?? ctx.periodLabel;
  const showCompany = props.showCompany ?? ctx.showCompany ?? isDatoxUser;
  const hasProject = !!projectLabel;
  const hasPeriod = !!periodLabel;

  // Project search: debounce typing so each keystroke doesn't fire a request.
  const [projectSearch, setProjectSearch] = useState('');
  const [debouncedProjectSearch, setDebouncedProjectSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedProjectSearch(projectSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [projectSearch]);

  // Project list — server-side search via the paginated workflows endpoint.
  // Cap to 12 so the dropdown stays a manageable height; users find anything
  // beyond that by typing in the search input (forwards to the API).
  const { workflows: projectWorkflows } = useGetWorkflows({
    pageSize: 12,
    search: debouncedProjectSearch,
    queryParams: { enabled: hasProject && !isApprover, keepPreviousData: true }
  }) as { workflows: any[] };

  // Periods of the current workflow — pages already fetch this, so this is a cache hit.
  const { data: currentWorkflow, refetch: refetchWorkflow } = useGetWorkflowById({
    id: ctx.workflowId,
    queryParams: { enabled: !!ctx.workflowId && hasPeriod }
  }) as { data?: any; refetch: () => void };

  // A report is opened for one member state; then its own date is the one to show.
  const memberState = new URLSearchParams(location.search).get('ms');
  // The open period's date; none once it is filed.
  const periodDue = useMemo(() => {
    const period = ((currentWorkflow?.periods as any[]) ?? []).find(p => p?.id === ctx.periodId);
    if (!period || period.submit_progress_status === 'Completed') return null;
    const all = deadlineLabel(period);
    const one = memberState ? jurisdictionDeadline(period, memberState) : null;
    const due = one || all;
    if (!due || !all) return null;
    const headline = one ? `${one.name} · ${one.date}` : all.span;
    const status = due.relative ?? (due.days < 0 ? 'passed' : null);
    return { ...due, all, headline, status };
  }, [currentWorkflow, ctx.periodId, memberState]);

  // The date hangs on the calendar: hover for it, amber in the last week.
  const periodIcon = periodDue ? (
    <Tooltip
      label={
        <DeadlineTip
          id="topbar-period-due"
          due={periodDue.all}
          period={periodLabel as string}
          headline={periodDue.headline}
          status={periodDue.status}
          current={memberState}
        />
      }
      {...DEADLINE_TIP_PROPS}
      openDelay={150}
    >
      <DueIcon data-tone={periodDue.tone}>
        <TbCalendar size={16} />
      </DueIcon>
    </Tooltip>
  ) : undefined;

  const { data: approverWorkflows } = useGetApproverWorkflows({
    queryParams: { enabled: isApprover && hasPeriod }
  }) as { data?: any[] };

  const projectOptions = useMemo(() => {
    const items = (projectWorkflows as any[]) ?? [];
    return items.filter(w => w?.id).map(w => ({ label: w.name as string, value: w.id as string }));
  }, [projectWorkflows]);

  // Index the workflow rows by id so we can route via `checkToRedirect`
  // (single-period projects → Funds, grouped/multi-period → Quarters).
  const workflowById = useMemo(() => {
    const items = (projectWorkflows as any[]) ?? [];
    return items.reduce<Record<string, any>>((acc, w) => {
      if (w?.id) acc[w.id] = w;
      return acc;
    }, {});
  }, [projectWorkflows]);

  const periodOptions = useMemo(() => {
    const periods = (currentWorkflow?.periods as any[]) ?? [];
    return periods
      .filter(p => p?.id)
      .map(p => ({
        label: p.year && p.period ? `${p.year}, ${p.period}` : (p.id as string),
        value: p.id as string
      }));
  }, [currentWorkflow]);

  const [isNewPeriodOpen, setNewPeriodOpen] = useState(false);

  const periodsNewestFirst = useMemo(() => {
    const periods = (currentWorkflow?.periods as any[]) ?? [];
    return [...periods].sort(byNewest);
  }, [currentWorkflow]);

  const canAddPeriod =
    hasPeriod &&
    !isApprover &&
    !!currentWorkflow &&
    permissionPolicies.checkAccess({
      currentUserRole: currentUserState?.role,
      currentUserId: currentUserState?.azure_object_id,
      assignedTo: currentWorkflow?.assigned_to
    }) &&
    periodsNewestFirst[0]?.filing_type !== 'PF_FINAL';

  const handleCreatedPeriod = (created: any) => {
    setNewPeriodOpen(false);
    refetchWorkflow();
    if (!created?.id || !ctx.workflowId) return;
    const first =
      currentWorkflow?.report_type === REPORT_TYPES.CONVERSION
        ? WORKFLOW_STAGE_KEYS.SOURCE_FILES
        : WORKFLOW_STAGE_KEYS.AIFS;
    navigate(
      buildStagePath(first, ctx.workflowId, created.id, created, currentWorkflow) ||
        `/project/quaters/${ctx.workflowId}/${created.id}/add-aifs`
    );
  };

  const approverPeriodOptions = useMemo(() => {
    const seen = new Set<string>();
    return ((approverWorkflows as any[]) ?? [])
      .filter(item => item?.workflow_id === ctx.workflowId && item?.period_id)
      .filter(item => {
        if (seen.has(item.period_id)) return false;
        seen.add(item.period_id);
        return true;
      })
      .map(item => ({
        label: item.year && item.period ? `${item.year}, ${item.period}` : item.period_id,
        value: item.period_id
      }));
  }, [approverWorkflows, ctx.workflowId]);

  const approverProjectOptions = useMemo(() => {
    const seen = new Set<string>();
    return ((approverWorkflows as any[]) ?? [])
      .filter(item => item?.workflow_id && item?.workflow_name)
      .filter(item => {
        if (seen.has(item.workflow_id)) return false;
        seen.add(item.workflow_id);
        return true;
      })
      .map(item => ({
        label: item.workflow_name,
        value: item.workflow_id
      }));
  }, [approverWorkflows]);

  const handleSelectProject = (workflowId: string) => {
    // Route via `checkToRedirect` so the destination matches the workflow's
    // shape — Conversion / grouped / multi-period workflows land on the
    // Quarters page, single-period workflows jump straight to Funds, and
    // child workflows hop to their group's grouped view. Falls back to the
    // Quarters page if the row is missing or doesn't resolve.
    const workflow = workflowById[workflowId];
    const target = checkToRedirect(workflow);
    if (target) {
      navigate(target);
      return;
    }
    const groupedSuffix = workflow?.is_workflow_group ? '?type=grouped' : '';
    navigate(`/project/quaters/${workflowId}${groupedSuffix}`);
  };

  const handleSelectPeriod = async (newPeriodId: string) => {
    if (!ctx.periodId || newPeriodId === ctx.periodId) return;
    const wfId = ctx.workflowId;
    if (!wfId) return;

    const fallback = `/project/quaters/${wfId}/${newPeriodId}/add-aifs`;
    const destPeriod = ((currentWorkflow?.periods as any[]) ?? []).find(p => p?.id === newPeriodId);
    if (!destPeriod) {
      navigate(fallback);
      return;
    }

    // Where is the user right now? If they're on a specific stage page
    // (Report / Output Tables / Extraction / etc.) we try to take them to
    // the same stage in the destination period; otherwise we fall back to
    // AddAIFs. The open file and sheet come too, matched by name — see below.
    const requestedStage = getStageFromLocation(location.pathname, location.search);

    // Report / Approval / Submission URLs include the destination's FCA vs
    // ESMA segment, which depends on the first jurisdiction's
    // reporting_member_state. Form PF + Conversion-with-no-jurisdictions
    // skip the fetch — the helper handles both paths internally.
    const reportFamily =
      requestedStage === WORKFLOW_STAGE_KEYS.REPORT ||
      requestedStage === WORKFLOW_STAGE_KEYS.APPROVAL ||
      requestedStage === WORKFLOW_STAGE_KEYS.SUBMISSION;
    const needsJurisdictions =
      reportFamily && currentWorkflow?.report_type !== REPORT_TYPES.FORM_PF;
    let destJurisdictions: any[] | undefined;
    if (needsJurisdictions) {
      try {
        destJurisdictions = await queryClient.fetchQuery(
          ['GET_JURISDICTIONS', newPeriodId, {}],
          () => jurisdictionService.getJurisdictions(newPeriodId, {})
        );
      } catch {
        destJurisdictions = undefined;
      }
    }

    const dest = pickPeriodSwitchDestination({
      requestedStage,
      workflowId: wfId,
      newPeriodId,
      destPeriod,
      project: currentWorkflow,
      destJurisdictions
    });

    navigate((await carryOpenWork(dest, requestedStage, newPeriodId)) || dest || fallback);
  };

  /**
   * What you had open, in the period you switched to.
   *
   * Switching period used to drop it from the address, which on any of these stages means arriving
   * at whatever the page picks first — usually not the sheet you were comparing against last
   * quarter, which is the reason you switched. The ids cannot carry (each period holds its own
   * rows), but the names can, and the names are what the client kept the same.
   *
   * Each stage names what it has open its own way, so each is carried its own way: Extraction and
   * Lookup by file and sheet, Transformation by the extraction those two produced, Output Data by
   * the table and category it writes — those two are names already and cross as they are.
   *
   * Only when the destination is the stage that was asked for: if the period has not got that far
   * and we fell back to an earlier one, any of this on the address would name something that stage
   * never reads.
   */
  const carryOpenWork = async (
    dest: string | undefined,
    requestedStage: string | null | undefined,
    newPeriodId: string
  ) => {
    if (!dest || !ctx.periodId) return undefined;

    const [destPath, destQuery = ''] = dest.split('?');
    if (getStageFromLocation(destPath, `?${destQuery}`) !== requestedStage) return undefined;

    const here = new URLSearchParams(location.search);

    if (requestedStage === WORKFLOW_STAGE_KEYS.OUTPUT_DATA) {
      return withQuery(dest, {
        outputTable: here.get('outputTable'),
        outputCategory: here.get('outputCategory')
      });
    }

    if (requestedStage === WORKFLOW_STAGE_KEYS.TRANSFORMATION) {
      return carryOpenExtraction(dest, here, newPeriodId);
    }

    if (!stageCarriesSheet(requestedStage)) return undefined;

    const fileId = here.get('fileId');
    if (!fileId) return undefined;
    const sheetId = here.get('sheetId') ?? undefined;

    const mapped = await mappedAcrossPeriods(fileId, sheetId, newPeriodId);
    if (mapped?.sheetId) return withFileSheet(dest, mapped);

    try {
      // Both sides through the cache the pages already fill, so the common case costs no request.
      const [fromFiles, toFiles] = await Promise.all(
        [ctx.periodId, newPeriodId].map(periodId =>
          queryClient.fetchQuery(['GET_PERIOD_FILES', periodId, {}], () =>
            periodService.getPeriodFiles(periodId, {})
          )
        )
      );
      return withFileSheet(
        dest,
        mapFileSheetAcrossPeriods(fromFiles, toFiles, fileId, sheetId) ?? mapped
      );
    } catch {
      // The switch itself is not worth failing over a lookup: land on the stage with whatever the
      // period mapping gave, which may be nothing.
      return withFileSheet(dest, mapped);
    }
  };

  /**
   * The file and sheet as the periods themselves record them.
   *
   * Source Files is where a new period's workbooks are paired with the previous period's — that is
   * what the step is for, and `automation-fund-mappings` returns the pairing, file by file and sheet
   * by sheet. So this is not a guess, and it is right where the names are not: the workbook is named
   * after the period it covers as often as not.
   *
   * Neighbours only. The pairing points one period back, so a jump over a quarter would have to walk
   * the chain, and the names answer that case well enough.
   */
  const mappedAcrossPeriods = async (
    fileId: string,
    sheetId: string | undefined,
    newPeriodId: string
  ) => {
    const periods = (currentWorkflow?.periods as any[]) ?? [];
    const here = periods.find(period => period?.id === ctx.periodId);
    const there = periods.find(period => period?.id === newPeriodId);

    const forward = there?.previous_period_id === ctx.periodId;
    const backward = here?.previous_period_id === newPeriodId;
    if (!forward && !backward) return null;

    // The pairing belongs to the later of the two periods — the one that was mapped onto the other.
    const mappingPeriodId = forward ? newPeriodId : ctx.periodId;
    try {
      // Same key the Source Files page uses, so a switch made from there costs no request.
      const mappings = await queryClient.fetchQuery(
        ['GET_AUTOMATION_FUND_MAPPINGS', mappingPeriodId, {}],
        () => automationService.getFundMappings(mappingPeriodId, {})
      );
      return mapFileSheetByMappings(mappings, fileId, sheetId, forward);
    } catch {
      return null;
    }
  };

  /**
   * The Transformation half of the above. It opens on an extraction, not on a sheet, so the sheet
   * crosses first — by the periods' own pairing, or failing that by the names — and the extraction
   * is then the one sitting on it in the same category. The table keeps its name: the page falls
   * back to its own first table when that name is not in the new period.
   */
  const carryOpenExtraction = async (
    dest: string,
    here: URLSearchParams,
    newPeriodId: string
  ): Promise<string | undefined> => {
    const extractId = here.get('extractId');
    if (!extractId || !ctx.workflowId) return undefined;

    try {
      // Same key the page's own query uses, so the period you are leaving costs no request.
      const [fromFiles, toFiles] = await Promise.all(
        [ctx.periodId, newPeriodId].map(periodId => {
          const params = { workflow_id: ctx.workflowId, period_id: periodId };
          return queryClient.fetchQuery([GET_WORKFLOW_EXTRACT, params], () =>
            workflowService.getWorkflowExtract(params)
          );
        })
      );
      // The sheet crosses first — by the pairing where there is one — and the extraction is then
      // the one on that sheet in this category.
      const from = findExtractLocation(fromFiles, extractId);
      const bySheet = from
        ? await mappedAcrossPeriods(from.fileId, from.sheetId, newPeriodId)
        : null;
      const mapped = mapExtractAcrossPeriods(fromFiles, toFiles, extractId, bySheet);
      if (!mapped?.extractId) return undefined;
      return withQuery(dest, {
        extractId: mapped.extractId,
        extractedTransformationTable: here.get('extractedTransformationTable')
      });
    } catch {
      return undefined;
    }
  };

  const handleSelectApproverPeriod = async (newPeriodId: string) => {
    if (!ctx.workflowId || newPeriodId === ctx.periodId) return;

    const record = ((approverWorkflows as any[]) ?? []).find(
      item => item?.workflow_id === ctx.workflowId && item?.period_id === newPeriodId
    );
    if (!record) return;

    await navigateToApproverRecord(record);
  };

  const handleSelectApproverProject = async (workflowId: string) => {
    if (workflowId === ctx.workflowId) return;

    const record = ((approverWorkflows as any[]) ?? []).find(
      item => item?.workflow_id === workflowId
    );
    if (!record) return;

    await navigateToApproverRecord(record);
  };

  const openApproverReport = useOpenApproverReport();
  const navigateToApproverRecord = (record: any) =>
    openApproverReport(record, {
      onMissing: () => toast.error('No report jurisdiction is available for this review.'),
      onError: () => navigate('/project')
    });

  const handleAddProject = () => {
    navigate('/project?createProject=1');
  };

  return (
    <TopBar>
      <NavLink
        id="datox-home-logo-link"
        className="logo-link"
        to="/project"
        aria-label="Datox home"
      >
        <img id="datox-home-logo" className="logo" src={logoUrl} alt="Datox" />
      </NavLink>

      <BreadcrumbGroup>
        {showCompany && (
          <CompanyBreadcrumb
            label={companyLabel ?? 'Company'}
            options={companyOptions}
            onSelect={handleSelectCompany}
            activeValue={currentCompanyId}
          />
        )}
        {showCompany && hasProject && <Separator>/</Separator>}
        {hasProject && !isApprover && (
          <ProjectBreadcrumb
            label={projectLabel as string}
            options={projectOptions}
            onSelect={handleSelectProject}
            footerAction={{ label: 'Add Project', onClick: handleAddProject }}
            activeValue={ctx.workflowId}
            searchValue={projectSearch}
            onSearchChange={setProjectSearch}
          />
        )}
        {hasProject && isApprover && approverProjectOptions.length > 1 && (
          <ProjectBreadcrumb
            label={projectLabel as string}
            options={approverProjectOptions}
            onSelect={handleSelectApproverProject}
            activeValue={ctx.workflowId}
          />
        )}
        {hasProject && isApprover && approverProjectOptions.length <= 1 && (
          <Chip type="button" disabled>
            <TbFolder size={16} />
            <span className="label">{projectLabel}</span>
          </Chip>
        )}
        {hasProject && hasPeriod && <Separator>/</Separator>}
        {hasPeriod && !isApprover && (
          <PeriodBreadcrumb
            label={periodLabel as string}
            options={periodOptions}
            onSelect={handleSelectPeriod}
            activeValue={ctx.periodId}
            icon={periodIcon}
            footerAction={
              canAddPeriod
                ? { label: 'New period', onClick: () => setNewPeriodOpen(true) }
                : undefined
            }
          />
        )}
        {hasPeriod && isApprover && approverPeriodOptions.length > 1 && (
          <PeriodBreadcrumb
            label={periodLabel as string}
            options={approverPeriodOptions}
            onSelect={handleSelectApproverPeriod}
            activeValue={ctx.periodId}
            icon={periodIcon}
          />
        )}
        {hasPeriod && isApprover && approverPeriodOptions.length <= 1 && (
          <Chip type="button" disabled>
            {periodIcon ?? <TbCalendar size={16} />}
            <span className="label">{periodLabel}</span>
          </Chip>
        )}
      </BreadcrumbGroup>

      <RightSlot>
        <ProfileSlot>
          <Profile open={false} avatarSize="24px" />
        </ProfileSlot>
      </RightSlot>

      {/* Mounted while the offer stands, not while the drawer is open: a Drawer that is mounted
          already open has no closed state to slide from, and appears in place. */}
      {canAddPeriod && (
        <NewPeriodBlock
          isOpen={isNewPeriodOpen}
          workflowId={ctx.workflowId}
          workflow={currentWorkflow}
          periods={periodsNewestFirst}
          reportType={currentWorkflow?.report_type}
          onCancel={() => setNewPeriodOpen(false)}
          onDone={handleCreatedPeriod}
        />
      )}
    </TopBar>
  );
};

export default AppTopBar;
